import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Sparkles, Lock, ArrowRight, Brain, FileQuestion, Coins, Trophy, Gauge } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/posthog";

/**
 * Lightweight head-tag manager. We don't pull in react-helmet-async
 * just for this one route — it'd add ~14 KB gzipped. This sets
 * <title>, <meta>, <link rel=canonical> and JSON-LD directly via
 * `document` and cleans them up on unmount so SPA navigation between
 * preview and other routes doesn't leave stale tags around.
 */
function useDocumentHead({ title, description, canonical, ogImage, jsonLd }) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];
    const upsertMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      let isNew = false;
      if (!el) {
        el = document.createElement(attrs.tag);
        document.head.appendChild(el);
        isNew = true;
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === "tag" || v == null) return;
        el.setAttribute(k, v);
      });
      if (isNew) created.push(el);
    };
    if (description) {
      upsertMeta('meta[name="description"]', { tag: "meta", name: "description", content: description });
      upsertMeta('meta[property="og:description"]', { tag: "meta", property: "og:description", content: description });
      upsertMeta('meta[name="twitter:description"]', { tag: "meta", name: "twitter:description", content: description });
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', { tag: "meta", property: "og:title", content: title });
      upsertMeta('meta[name="twitter:title"]', { tag: "meta", name: "twitter:title", content: title });
    }
    if (canonical) {
      upsertMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });
      upsertMeta('meta[property="og:url"]', { tag: "meta", property: "og:url", content: canonical });
    }
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', { tag: "meta", property: "og:image", content: ogImage });
      upsertMeta('meta[name="twitter:image"]', { tag: "meta", name: "twitter:image", content: ogImage });
    }
    upsertMeta('meta[name="twitter:card"]', { tag: "meta", name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[property="og:type"]', { tag: "meta", property: "og:type", content: "article" });

    let jsonLdEl = null;
    if (jsonLd) {
      jsonLdEl = document.createElement("script");
      jsonLdEl.type = "application/ld+json";
      jsonLdEl.dataset.preview = "1";
      jsonLdEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdEl);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
      if (jsonLdEl) jsonLdEl.remove();
    };
  }, [title, description, canonical, ogImage, jsonLd]);
}

/**
 * Public Preview page — `/p/:slug`.
 *
 * For HUMAN users, this React route gives them the polished SPA UX
 * (matching fonts, glassmorphism cards, the Quill brand). For BOTS,
 * the same content is served via the SSR HTML route at /api/p/{slug}
 * — so AI search engines (GPTBot, ClaudeBot, Perplexity, Gemini,
 * Googlebot, OAI-SearchBot) can crawl the first 10% without
 * executing JS.
 *
 * The "Unlock" CTA pushes to /login?resume_path={path_id} which
 * ResumePathBridge picks up post-auth to deep-link the learner
 * straight into the full reading pane.
 */
const SHARE_HASHTAGS = "OAKAcademy,AILearning,EdTech";

export default function PublicPreview() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    trackEvent("preview_viewed", { slug });
    let cancelled = false;
    api.get(`/preview/data/${slug}`)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? "not_found" : "error");
      });
    return () => { cancelled = true; };
  }, [slug]);

  // Persist the path_id in localStorage immediately so even if the user
  // bounces away to Google sign-in via the URL bar, the resume bridge
  // still fires correctly post-auth.
  useEffect(() => {
    if (!data?.path_id) return;
    try { localStorage.setItem("oak_resume_path", data.path_id); } catch {}
  }, [data?.path_id]);

  const onUnlock = () => {
    trackEvent("preview_unlock_clicked", { slug, path_id: data?.path_id });
    if (data?.path_id) {
      try { localStorage.setItem("oak_resume_path", data.path_id); } catch {}
    }
    navigate(`/login?resume_path=${encodeURIComponent(data?.path_id || "")}`);
  };

  const canonical = typeof window !== "undefined"
    ? `${window.location.origin}/p/${slug}`
    : `https://oakaiapp.com/p/${slug}`;

  // Compose JSON-LD client-side too (for users who deep-link; this is a
  // backup for the SSR JSON-LD already in /api/p/{slug}).
  const jsonLd = useMemo(() => {
    if (!data) return null;
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Course",
          "name": data.goal,
          "description": data.summary,
          "url": canonical,
          "isAccessibleForFree": false,
          "inLanguage": data.language,
          "provider": {
            "@type": "Organization",
            "name": "Oak AI Campus",
            "url": typeof window !== "undefined" ? window.location.origin : "https://oakaiapp.com",
          },
          "hasPart": (data.steps || []).map((s) => ({
            "@type": "LearningResource",
            "name": s.title,
            "isAccessibleForFree": s.index === 0,
          })),
        },
      ],
    };
  }, [data, canonical]);

  // Head management — writes title/meta/og/JSON-LD directly to <head>.
  useDocumentHead({
    title: data ? `${data.goal} · Oak AI Campus` : null,
    description: data ? (data.summary || "").slice(0, 200) : null,
    canonical: data ? canonical : null,
    ogImage: "/brand/oak-social-1200x630.png",
    jsonLd,
  });

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white grid place-items-center p-6">
        <div className="text-center max-w-md">
          <div className="text-5xl mb-4">🔍</div>
          <h1 className="font-display text-2xl mb-2">This preview isn't published</h1>
          <p className="text-white/60 text-sm mb-5">The path with slug <code className="text-white/80">{slug}</code> doesn't exist or hasn't been made public yet.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-[#0B0E14] font-display font-semibold text-sm">
            Back to Oak AI Campus <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white grid place-items-center">
        <div className="animate-pulse text-white/50 font-display">Loading preview…</div>
      </div>
    );
  }

  const tier = (data.tier || "intermediate").toUpperCase();
  const lang = (data.language || "en").toUpperCase();

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white">
      {/* Slim public header — guest-friendly, no sidebar/menu chrome.
          Keeps the preview clean for first-time visitors arriving from search. */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-[#0B0E14]/80 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#0044FF] to-[#00FF9D] grid place-items-center font-display font-bold text-sm">O</div>
            <div className="font-display font-bold tracking-tight group-hover:text-[#00FF9D] transition">Oak AI Campus</div>
          </Link>
          <button
            type="button"
            onClick={onUnlock}
            className="px-4 py-2 rounded-full bg-[#0044FF] hover:bg-[#0035cc] text-white text-xs font-display font-semibold transition"
            data-testid="preview-cta-header"
          >
            Sign in with Google
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
        {/* Breadcrumb trail — mirrors the BreadcrumbList JSON-LD and gives
            users a one-click path back to the broader library. Critical
            for crawl-graph density: every preview page links to /explore. */}
        <nav className="flex flex-wrap items-center gap-2 text-xs text-white/45 mb-5" data-testid="preview-breadcrumbs">
          <Link to="/" className="hover:text-white/85 transition">Oak AI Campus</Link>
          <span className="text-white/20">›</span>
          <Link to="/explore" className="hover:text-white/85 transition">Explore Library</Link>
          <span className="text-white/20">›</span>
          <span className="text-white/85 truncate max-w-[260px] md:max-w-md">{data.goal}</span>
        </nav>

        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/20 text-[#00FF9D] text-[11px] font-display font-semibold tracking-wide uppercase mb-5">
          <Sparkles size={11} /> AI-Guided Mastery · Public Preview
        </div>
        <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mb-4" data-testid="preview-title">
          {data.goal}
        </h1>
        <p className="text-white/65 text-base md:text-lg max-w-2xl mb-8">
          {data.summary || `A 5-step AI-generated mastery path on ${data.goal}.`}
        </p>

        {/* Meta chips */}
        <div className="flex flex-wrap gap-2 mb-12">
          <Chip Icon={Brain} label={`${data.steps?.length || 5} Steps`} />
          <Chip Icon={Gauge} label={`${tier} tier`} />
          <Chip Icon={FileQuestion} label="Adaptive Exam" />
          <Chip Icon={Trophy} label="Shareable Cert" />
          <Chip Icon={Coins} label="6 free credits to unlock" />
        </div>

        {/* Steps */}
        <section className="space-y-5" data-testid="preview-steps-list">
          {(data.steps || []).map((step) => (
            <StepCard key={step.index} step={step} totalSteps={data.steps.length} onUnlock={onUnlock} />
          ))}
        </section>

        {/* The big "Unlock" card */}
        <UnlockCard onUnlock={onUnlock} />

        <PreviewFooter slug={slug} />
      </main>
    </div>
  );
}

function Chip({ Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-white/75">
      <Icon size={12} className="text-white/55" />
      <span className="font-medium">{label}</span>
    </span>
  );
}

function StepCard({ step, totalSteps, onUnlock }) {
  const isFree = step.index === 0;
  return (
    <article
      className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-7 overflow-hidden"
      data-testid={`preview-step-${step.index}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`text-[11px] font-display font-semibold tracking-wider uppercase ${isFree ? "text-[#00FF9D]" : "text-white/40"}`}>
          Step {step.index + 1} of {totalSteps} · {isFree ? "Free preview" : "Locked"}
        </div>
        {!isFree && <Lock size={14} className="text-white/40" />}
      </div>
      <h2 className="font-display text-xl md:text-2xl font-semibold tracking-tight mb-2">{step.title}</h2>
      {step.objective && <p className="text-white/55 text-sm mb-3">{step.objective}</p>}
      {!!(step.key_terms || []).length && (
        <div className="flex flex-wrap gap-1.5 mb-4">
          {step.key_terms.slice(0, 6).map((t, i) => (
            <span key={i} className="text-[11px] px-2 py-1 rounded bg-[#0044FF]/10 border border-[#0044FF]/20 text-[#9bb8ff]">{t}</span>
          ))}
        </div>
      )}

      {isFree && step.preview_md ? (
        <div
          className="prose prose-invert prose-sm max-w-none text-white/85"
          data-testid="preview-md-body"
          dangerouslySetInnerHTML={{ __html: lightMd(step.preview_md) }}
        />
      ) : (
        // Glassmorphism gated body — DOM still has the structure so SEO
        // bots see "this section exists" but humans see only blur.
        <div className="relative rounded-xl overflow-hidden" data-testid="preview-gated-body">
          <div
            className="text-white/30 select-none pointer-events-none max-h-[180px] overflow-hidden"
            style={{ filter: "blur(7px)" }}
            aria-hidden="true"
          >
            <p className="mb-2">In this whitepaper you'll explore frameworks, examples, simulations, and a Live Doubt Solver tutor that already knows your exact course material.</p>
            <p className="mb-2">Topics covered include the foundational concepts, real-world case studies, regulatory landscape, and forward-looking strategy patterns. Plus highlight-clip-to-OAK-Pad notes.</p>
            <p className="mb-2">Interactive simulations let you stress-test the ideas, while analogies remap the concept into a domain you're already fluent in — sports, biology, music, history, art.</p>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0E14]/60 to-[#0B0E14]" />
          <button
            type="button"
            onClick={onUnlock}
            className="absolute inset-x-0 bottom-3 mx-auto w-fit inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 hover:bg-white text-[#0B0E14] text-xs font-display font-semibold transition"
            data-testid={`btn-unlock-step-${step.index}`}
          >
            <Lock size={12} /> Unlock with 6 free credits
          </button>
        </div>
      )}
    </article>
  );
}

function UnlockCard({ onUnlock }) {
  const features = [
    "🧠 30-min whitepapers per step",
    "💬 Live Doubt Solver — AI tutor that knows your course",
    "🎬 Simulations & cross-domain analogies",
    "📝 Adaptive 5-MCQ exam per path",
    "📜 Shareable certificate",
    "🎧 OAK-Pad highlight clipper",
  ];
  return (
    <aside
      className="mt-10 md:mt-14 rounded-3xl border border-[#00FF9D]/25 bg-gradient-to-br from-[#0044FF]/15 via-[#0B0E14]/60 to-[#00FF9D]/10 p-7 md:p-10 backdrop-blur-xl text-center"
      data-testid="preview-unlock-card"
    >
      <h3 className="font-display text-2xl md:text-4xl font-bold tracking-tight mb-3">
        Unlock the full path with 6 free credits
      </h3>
      <p className="text-white/70 text-sm md:text-base max-w-xl mx-auto mb-7">
        Sign in with Google — get 6 credits on the house, plus 2 fresh credits every week. No card required.
      </p>
      <div className="flex flex-wrap justify-center gap-2 mb-7">
        {features.map((f) => (
          <span key={f} className="text-xs px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/10 text-white/85">{f}</span>
        ))}
      </div>
      <button
        type="button"
        onClick={onUnlock}
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-[#0B0E14] font-display font-bold text-base hover:scale-[1.03] transition"
        data-testid="preview-cta-unlock"
      >
        Unlock the full path <ArrowRight size={16} />
      </button>
      <div className="text-[11px] text-white/45 mt-3">By continuing you agree to our Terms. The first 6 credits are on us.</div>
    </aside>
  );
}

function PreviewFooter({ slug }) {
  return (
    <footer className="mt-16 pt-6 border-t border-white/5 text-xs text-white/40 flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
      <Link to="/" className="hover:text-white/80 transition">Oak AI Campus</Link>
      <span>·</span>
      <a href="/api/og/sitemap.xml" className="hover:text-white/80 transition">Sitemap</a>
      <span>·</span>
      <a href={`/api/p/${slug}`} className="hover:text-white/80 transition">SSR view</a>
      <span>·</span>
      <Link to="/privacy" className="hover:text-white/80 transition">Privacy</Link>
    </footer>
  );
}

/**
 * Tiny markdown renderer — enough for h1/h2/h3, **bold**, *italic*,
 * `code`, lists, and paragraphs. We keep it tight to avoid pulling in
 * a 30 KB markdown lib for what is, in the preview, a 600-character snippet.
 */
function lightMd(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inUl = false;
  for (let raw of lines) {
    const line = raw.replace(/\s+$/, "");
    if (!line) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      continue;
    }
    const m = line.match(/^(#{1,3})\s+(.+)$/);
    if (m) {
      if (inUl) { out.push("</ul>"); inUl = false; }
      const level = m[1].length;
      out.push(`<h${level}>${escapeHtml(m[2])}</h${level}>`);
      continue;
    }
    if (/^[\*\-]\s+/.test(line)) {
      if (!inUl) { out.push("<ul>"); inUl = true; }
      out.push(`<li>${formatInline(line.replace(/^[\*\-]\s+/, ""))}</li>`);
      continue;
    }
    if (inUl) { out.push("</ul>"); inUl = false; }
    out.push(`<p>${formatInline(line)}</p>`);
  }
  if (inUl) out.push("</ul>");
  return out.join("\n");
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

function formatInline(s) {
  let esc = escapeHtml(s);
  esc = esc.replace(/\*\*([^\*]+?)\*\*/g, "<strong>$1</strong>");
  esc = esc.replace(/(?<!\*)\*([^\*]+?)\*(?!\*)/g, "<em>$1</em>");
  esc = esc.replace(/`([^`]+)`/g, "<code>$1</code>");
  return esc;
}
